import { useSuspenseQuery } from '@tanstack/react-query';
import { ISbStoryData } from 'storyblok-js-client';

import { getSlugWithDash } from '@natu/utils/getSlugWithDash';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getStoryblokSdk } from '../../../../../api/api';
import { useDraftModeContext } from '../../../../../storyblok-preview';
import { StoryblokAsset } from '../../../../../utils';

type GetArticleShowcaseQueryKeyInput = {
  draftMode: boolean;
  items?: string[];
  category?: string;
  perPage?: number;
};

export const getArticleShowcaseQueryKey = ({
  draftMode,
  items,
  category,
  perPage,
}: GetArticleShowcaseQueryKeyInput) => ['articleShowcase', items, category, draftMode, perPage];

type ArticleShowcaseItem = {
  title?: string;
  description?: string;
  previewImage?: StoryblokAsset;
  timeToRead?: string;
  uuid?: string;
  firstPublishedAt?: string;
  fullSlug?: string;
};

type GetArticleShowcaseQueryInput = {
  draftMode: boolean;
  items?: string[];
  category?: string;
  perPage?: number;
};

export const getArticleShowcaseQuery = async ({
  items = [],
  draftMode = false,
  category = '',
  perPage = 3,
}: GetArticleShowcaseQueryInput): Promise<ArticleShowcaseItem[]> => {
  const { getArticleNodes } = getStoryblokSdk({ draftMode });

  const { data } = await getArticleNodes({
    startsWith: 'blog',
    sortBy: 'first_published_at:desc',
    perPage: perPage ?? 3,
    filterQuery: {
      component: {
        in: 'article',
      },
      ...(category &&
        items?.length === 0 && {
          categories: {
            any_in_array: category,
          },
        }),
    },
    byUuidsOrdered: items?.join(',') || '',
    excludingFields:
      'body,afterBody,beforeBody,customPreHeader,customHeader,seo,author,categories,category,advancedSeo,disablePreHeader',
  });

  return data?.stories.map((story: ISbStoryData) => ({
    title: story?.content?.title,
    description: story?.content?.description,
    previewImage: story?.content?.previewImage,
    timeToRead: story?.content?.timeToRead,
    uuid: story?.uuid,
    firstPublishedAt: story?.first_published_at,
    fullSlug: getSlugWithDash(story?.full_slug),
  }));
};

type UseCategoryBarInput = Omit<GetArticleShowcaseQueryKeyInput, 'draftMode'>;

export const useArticleShowCase = ({ category, items, perPage }: UseCategoryBarInput) => {
  const { draftMode } = useDraftModeContext();

  const { data } = useSuspenseQuery({
    queryKey: getArticleShowcaseQueryKey({ category, items, draftMode, perPage }),
    queryFn: async () => getArticleShowcaseQuery({ category, items, draftMode, perPage }),
  });

  return {
    data,
    isEmpty: !isArrayWithLength(data),
  };
};
